/* helper function for init functions */
import {isLoggedIn, loginWithUniversalLink} from "@/helper/auth";

import {useCyclicRefresh} from "@/helper/cyclicRefresh"

import {registerToPushNotifications} from "@/helper/pushNotifications";
import {deviceProfileRefresh} from "@/helper/deviceProfile";

import {init as initNotifications} from '@/helper/notification';
import {useAuthStore} from '@/store/auth';
import {User} from "@/graphql/generated/graphql";
import {useGlobalEmitter} from "./emitter";
import {consoleLogApp} from "@/helper/console";
import {computed, ref, Ref} from "vue";
import router from "@/router";
import {promiseTimeout} from "@vueuse/core";
import {timerPromise} from "@/helper/timerPromise";
import {setUpCandidate} from "@/helper/candidate";
import {useChatStore} from "@/helper/chat/chatStore";
import {URLOpenListenerEvent} from "@capacitor/app";
import {alertController} from "@ionic/vue";

export type AppState = 'loggedout' | 'token' |  'employee' | 'candidate';

interface State {
    index: number,
    key: AppState,
    next?: (...args : any[]) => void,
    prev?: () => void,
    beforeStateLeave?:() => Promise<void>,
    afterStateLeave?:() => Promise<void>,
    beforeStateEnter?:() => Promise<void>,
    afterStateEnter?: () => Promise<void>,
}

const states = [
    {
        // 1: not logged in
        index: 0,
        key: 'loggedout',
    } as State,
    {
        // 2: logged in (as employee, candidate or consultant)
        index: 1,
        key: 'token',
        prev: () => {},
    } as State,
    {
        // 3a: employee defined (as employee or consultant)
        index: 2,
        key: 'employee',
        prev: () => {},
        afterStateEnter: async () => {
            /* setup cyclic refresh */      
            await useCyclicRefresh().start()            
            consoleLogApp("started cyclic refresh")

            /* in app notification (from push notification) handling */
            const authStore = useAuthStore()
            if(authStore.isUserRole('employee')) {
                initNotifications()
                consoleLogApp("initialized in app notifications")
            }

            /* fcm token, device profile & push notifications */
            if(authStore.isUserRole('employee')){
                consoleLogApp("registering push notifications & device profile for push notifications")
                if (!authStore.didRequestForPushNotificationsPermission) {
                    // open prompt which calls registerToPushNotifications once the user confirms that he wants notifications
                    authStore.needsPromptForPushNotificationsPermission = true
                    return false
                }
                try {
                    const success = await registerToPushNotifications()

                    // no await because not needed
                    if (success) {
                        deviceProfileRefresh(true)
                    }
                } catch (e : any) {
                    consoleLogApp("registering push notifications failed: " + (e?.message || e || ''))
                }
            }
        },
        beforeStateLeave: async () => {
            useCyclicRefresh().stop()
            consoleLogApp("stopped cyclic refresh")
        }
    } as State,
    {
        // 3b: candidate defined
        index: 2,
        key: 'candidate',
        prev: () => {},
        afterStateEnter: async () => {
            /* setup cyclic refresh */
            await useCyclicRefresh().start()
            consoleLogApp("started cyclic refresh")
        },
        beforeStateLeave: async () => {
            useCyclicRefresh().stop()
            consoleLogApp("stopped cyclic refresh")
        }
    } as State,
]

const findState = (key: AppState) : State => {
    const s = states.find(state => key === state.key) 
    if (!s) {
        throw new Error('state not found')
    }
    return s
}

let state : Ref<State|undefined> = ref(undefined);
const setState = async (key:AppState, impersonationUserSwitch = false) => {
    if (state.value && state.value.key === key) {
        throw new Error('state already set')
    }

    const newState = findState(key)
    const oldState = state.value || undefined

    const direction = !oldState || newState.index > oldState.index ? 'up' : 'down'

    if (oldState?.beforeStateLeave) {
        await oldState.beforeStateLeave()
    }

    if (newState?.beforeStateEnter) {
        await newState.beforeStateEnter()
    }

    state.value = newState

    if (oldState?.afterStateLeave) {
        await oldState.afterStateLeave()
    }
    if (newState?.afterStateEnter) {
        await newState.afterStateEnter()
    }

    consoleLogApp('app state ' + direction + ' ' + (oldState ? (oldState.key + ' > ') : '') + newState.key)

    useGlobalEmitter().emit("AppStateChanged", {state: state.value?.key, impersonationUserSwitch})
}


const _isHandlingUniversalLinkOrCandidateCreateNow = ref(false)
export const isHandlingUniversalLinkOrCandidateCreateNow = computed(() => _isHandlingUniversalLinkOrCandidateCreateNow.value)

let _universalLink: undefined|string = undefined
export const informAppStateAboutUniversalLink = (link:string) => {
    _universalLink = link
    if (isHandlingUniversalLinkOrCandidateCreateNow.value) {
        return // expect to be called again after is finished
    }
    _triggerUniversalLinkHandling()
}

const _triggerUniversalLinkHandling = async function () {
    if (!_universalLink)    {
        throw new Error('unset universal link')
    }

    _isHandlingUniversalLinkOrCandidateCreateNow.value = true

    // try to login with it (exception & alert otherwise)
    const link = _universalLink
    _universalLink = undefined
    try {
        await loginWithUniversalLink(link, false)
    } catch (e:any) {
        const alert = await alertController.create({
            header: 'Fehler',
            message: e?.message || 'Unbekannter Fehler aufgetreten',
            buttons: ['Ok'],
        });

        await alert.present();
    } finally {
        _isHandlingUniversalLinkOrCandidateCreateNow.value = false
    }
}

const _triggerCandidatePath = async () => {
    // wait for half a second for magiclink in parallel
    await timerPromise(500)

    // if we - after initial wait - are already promoted: cancel candidate create
    if (isLoggedIn() || isHandlingUniversalLinkOrCandidateCreateNow.value) {
        return
    }

    _isHandlingUniversalLinkOrCandidateCreateNow.value = true

    // promiseTimeout(10).then(() => useGlobalEmitter().emit('test'))
    await setUpCandidate()

    useChatStore().setIsFirstContact(true)

    _isHandlingUniversalLinkOrCandidateCreateNow.value = false

    // if we received a magiclink in parallel - handle this now
    if (_universalLink) {
        await _triggerUniversalLinkHandling()
    }
}

export function useAppState() {

    const onAuthLogin = async () => {
        await setState('token')
        if (useAuthStore().isImpersonating() || useAuthStore().isUserRole('employee')) {
            await setState('employee', false) // initial promotion after login - no user switch
        } else if (useAuthStore().isUserRole('candidate')) {
            await setState('candidate', false)
        }
    }

    const goToLogout = async () => {
        // dont skip a stepp
        if (state.value?.key === 'employee' || state.value?.key === 'candidate') {
            await setState('token')
        }
        if (state.value?.key !== 'loggedout') {
            await setState('loggedout')
        }
    }

    const onImpersonationStart = async () => {
        await setState('employee', true)
    }

    const onImpersonationStop = async () => {
        await setState('token', true)
    }

    const init = async () => {
        
        // initial state
        await setState('loggedout')

        if (isLoggedIn()) {
            // awaiting login
            await onAuthLogin()
        } else if (!isHandlingUniversalLinkOrCandidateCreateNow.value) {
            // dont wait for it to finish "init"
            _triggerCandidatePath()
        }
    }

    const role : Ref<'employee' | 'candidate' | undefined> = computed(() => {
        switch (state.value?.key) {
            case 'employee': return 'employee'
            case 'candidate': return 'candidate'
            default: return undefined
        }
    })

    return {
        init,
        // state: () => state,
        logout: async () => {
            useAuthStore().purgeAuth()

            await goToLogout()

            resetRouterBasedOnAppState()
        },
        login: async (token: string) => {
            const authStore = useAuthStore();
            authStore.setToken(token);

            const user = await authStore.refreshCurrentUser()
            if (!user) {
                throw new Error('user not found')
            }

            await onAuthLogin()

            // after successful login & role fetch: re-route            
            resetRouterBasedOnAppState()
        },
        impersonate: async (user: User) => {
            const authStore = useAuthStore()

            if (authStore.isImpersonating()) {                
                authStore.resetImpersonation()
                onImpersonationStop()
            }

            authStore.setImpersonation(user)
            
            await onImpersonationStart()
        },
        stopImpersonation: async () => {
            const authStore = useAuthStore()
            if (!authStore.isImpersonating()) {
                return
            }
            authStore.resetImpersonation()
            
            await onImpersonationStop()

            resetRouterBasedOnAppState()
        },
        employeeOrCandidate: role,
        getEmployeeOrCandidate: () : 'employee' | 'candidate' | undefined => role.value,
        isEmployeeOrCandidate: (employeeOrCandidate: 'employee' | 'candidate') : boolean => role.value === employeeOrCandidate,
        role
    };
}

export const resetRouterBasedOnAppState = () => {
    router.push(defaultRouteBasedOnAppState())
};

export const defaultRouteBasedOnAppState = () => {
    const appState = useAppState();
    const authStore = useAuthStore();
    switch (true) {
        case !isLoggedIn():
            return "/auth";
        case appState.isEmployeeOrCandidate('employee'):
            return '/chat';
        case appState.isEmployeeOrCandidate('candidate'):
            return '/public';
        case authStore.isUserRole('consultant'):
            return  "/employees";
        default:
            throw new Error('unknown app state config - no target for redirecting')
    }
}
